import { ModificarTipoCampoModal } from "@/components/modal/ModificarTipoCampoModal";
import { VerEstablecimientoMapModal } from "@/components/modal/VerEstablecimientoMapModal";
import { VerLoteMapModal } from "@/components/modal/VerLoteMapModal";
import { TipoCampoBadge } from "@/components/common/TipoCampoBadge";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import {
  EstablishmentDetailResponse,
  EstablishmentItem,
  EstablishmentLote,
} from "@/services/establishments.service";
import { AppColors } from "@/styles/colors";
import { Accordion, Box, Button, Field, Flex, Input, Text } from "@chakra-ui/react";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchEstablishmentDetailHandler,
  patchEstablishmentTypeHandler,
} from "./schema/handler-functions";
import { LotesTable } from "../components/LotesTable";



export const EstablishmentAndFieldsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<EstablishmentDetailResponse | null>(null);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingPatch, setLoadingPatch] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState<EstablishmentItem | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapEstablishment, setMapEstablishment] = useState<EstablishmentItem | null>(null);
  const [loteMapModalOpen, setLoteMapModalOpen] = useState(false);
  const [loteMapData, setLoteMapData] = useState<{ lote: EstablishmentLote; establishmentName: string; establishmentLotes: EstablishmentLote[] } | null>(null);

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive && id) {
      fetchEstablishmentDetailHandler({
        id,
        setLoading,
        setDetail,
        navigate,
        fetchPermissions,
      });
    }
  }, [isActive, id, navigate, fetchPermissions, setLoading, setDetail]);

  const toggleItem = (itemId: string) => {
    setOpenItems((prev) =>
      prev.includes(itemId) ? prev.filter((i) => i !== itemId) : [...prev, itemId],
    );
  };

  const handleVerEnMapaEstablecimiento = (est: EstablishmentItem) => {
    setMapEstablishment(est);
    setMapModalOpen(true);
  };

  const handleVerEnMapaLote = (lote: EstablishmentLote, establishmentName: string, establishmentLotes: EstablishmentLote[]) => {
    setLoteMapData({ lote, establishmentName, establishmentLotes });
    setLoteMapModalOpen(true);
  };

  const handleModificarTipo = (est: EstablishmentItem) => {
    setSelectedEstablishment(est);
    setModalOpen(true);
  };

  const handlePatchTipo = async (situacion: number) => {
    if (!selectedEstablishment || !id) return;
    await patchEstablishmentTypeHandler({
      idEstablecimiento: selectedEstablishment.id,
      situacion,
      setLoading: setLoadingPatch,
      navigate,
      fetchPermissions,
      onSuccess: () => {
        setModalOpen(false);
        fetchEstablishmentDetailHandler({ id, setLoading, setDetail, navigate, fetchPermissions, silent: true });
      },
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="20px" paddingBottom="32px" height="100%" overflowY="auto">
      <SectionHeader
        title="Maestro de establecimientos y lotes"
        backButton
        onBack={() => navigate(AppRoutes.ESTABLISHMENTS_MAIN_PAGE)}
        showButtons={false}
        loading={loading}
      />

      {!loading && detail && (
        <>
          <Field.Root maxWidth="400px">
            <Field.Label fontWeight={600}>Productor</Field.Label>
            <Input
              value={detail.nombreProductor}
              readOnly
              disabled
              height="40px"
              paddingX={"8px"}
              bg={AppColors.BACKGROUND_SECONDARY}
            />
          </Field.Root>

          <Accordion.Root
            multiple
            value={openItems}
            onValueChange={(details) => setOpenItems(details.value)}
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            borderRadius="6px"
            overflowY="auto"
          >
            {detail.establecimientos?.map((est) => {
              const isOpen = openItems.includes(est.id);
              return (
                <Accordion.Item
                  key={est.id}
                  value={est.id}
                  border="none"
                  borderBottom={`1px solid ${AppColors.WHITE_BORDER_1}`}
                >
                  <Flex alignItems="center" paddingY="14px" paddingX="12px">

                    <Flex
                      flex={1}
                      alignItems="center"


                      cursor="pointer"
                      onClick={() => toggleItem(est.id)}
                    >
                      <Flex flexDir={"column"} gap="8px">
                        <Text fontWeight={600} fontSize="15px">
                          {est.nombre}
                        </Text>
                        <Flex flexWrap="wrap" gap="14px">
                          <TipoCampoBadge tipo={est.tipoCampo} />
                          <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                            ID Agrobit: {est.id}
                          </Text>
                          <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                            {est.cantidadLotes} lotes
                          </Text>
                          <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                            {est.sumaHALotes ?? "-"} ha
                          </Text>
                        </Flex>
                      </Flex>


                    </Flex>

                    <Flex gap="8px" alignItems="center">
                      <Button
                        variant="outline"
                        height="32px"
                        paddingX="12px"
                        fontSize="13px"
                        fontWeight={600}
                        bgColor={AppColors.GRAY_BUTTON_BG}
                        onClick={() => handleModificarTipo(est)}
                      >
                        Modificar tipo
                      </Button>
                      <Button
                        variant="outline"
                        height="32px"
                        paddingX="12px"
                        fontSize="13px"
                        onClick={() => handleVerEnMapaEstablecimiento(est)}
                      >
                        <Text fontWeight={600}>
                          Ver en mapa
                        </Text>
                      </Button>
                      <Box
                        cursor="pointer"
                        onClick={() => toggleItem(est.id)}
                        display="flex"
                        alignItems="center"
                        paddingLeft="4px"
                      >
                        <ChevronDown
                          size={18}
                          style={{
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </Box>
                    </Flex>
                  </Flex>

                  <Accordion.ItemContent>
                    <Accordion.ItemBody paddingX="12px" paddingBottom="16px">
                      <LotesTable
                        lotes={est.lotes}
                        onVerEnMapa={(lote) => handleVerEnMapaLote(lote, est.nombre, est.lotes)}
                      />
                    </Accordion.ItemBody>
                  </Accordion.ItemContent>
                </Accordion.Item>
              );
            })}
          </Accordion.Root>
        </>
      )}

      <ModificarTipoCampoModal
        open={modalOpen}
        setOpen={setModalOpen}
        currentTipo={selectedEstablishment?.tipoCampo}
        loadingAccept={loadingPatch}
        onAccept={handlePatchTipo}
      />

      <VerEstablecimientoMapModal
        open={mapModalOpen}
        setOpen={setMapModalOpen}
        establishment={mapEstablishment}
        showPerimeter
      />

      <VerLoteMapModal
        open={loteMapModalOpen}
        setOpen={setLoteMapModalOpen}
        lote={loteMapData?.lote ?? null}
        establishmentName={loteMapData?.establishmentName}
        establishmentLotes={loteMapData?.establishmentLotes}
      />
    </Flex>
  );
};
